import {
  mockActivity,
  mockCertificates,
  mockDocuments,
  mockTimeline,
  type Activity,
  type Certificate,
  type TimelineEvent,
  type UploadedDocument,
} from "./mock";
import type { Role } from "@pahcertificar/certmanager-auth-core";

export type UserProfile = {
  firstName: string;
  secondName: string;
  firstSurname: string;
  secondSurname: string;
  email: string;
  documentType: "CC" | "CE" | "PA" | "NIT";
  documentNumber: string;
  city: string;
  department: string;
  address: string;
  kind: "natural" | "juridica";
  companyName: string | null;
};

export type UserData = {
  profile: UserProfile;
  certificates: Certificate[];
  documents: UploadedDocument[];
  activity: Activity[];
  timelines: Record<string, TimelineEvent[]>;
};

const KEY_PREFIX = "pahcertificar.app.user";

const slot = (userId: string, key: string) => `${KEY_PREFIX}.${userId}.${key}`;

const EMPTY_PROFILE: UserProfile = {
  firstName: "",
  secondName: "",
  firstSurname: "",
  secondSurname: "",
  email: "",
  documentType: "CC",
  documentNumber: "",
  city: "",
  department: "",
  address: "",
  kind: "natural",
  companyName: null,
};

const ADMIN_PROFILE: UserProfile = {
  firstName: "María",
  secondName: "Fernanda",
  firstSurname: "Gómez",
  secondSurname: "",
  email: "maria.gomez@ejemplo.com",
  documentType: "CC",
  documentNumber: "1.045.678.900",
  city: "Barranquilla",
  department: "Atlántico",
  address: "Carrera 53 # 82 - 10",
  kind: "natural",
  companyName: null,
};

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function loadUserData(userId: string, role: Role, phone: string): UserData {
  const existing = read<UserData>(slot(userId, "data"));
  if (existing) {
    const raw = existing.profile as UserProfile & { lastName?: string };
    const healed: UserProfile = {
      ...EMPTY_PROFILE,
      ...raw,
      secondName: raw.secondName ?? "",
      firstSurname: raw.firstSurname || (raw.lastName ? raw.lastName.split(/\s+/)[0] ?? "" : ""),
      secondSurname:
        raw.secondSurname ||
        (raw.lastName ? raw.lastName.split(/\s+/).slice(1).join(" ") : ""),
    };
    return {
      profile: healed,
      certificates: existing.certificates ?? [],
      documents: existing.documents ?? [],
      activity: existing.activity ?? [],
      timelines: existing.timelines ?? {},
    };
  }
  const seed: UserData =
    role === "admin"
      ? {
          profile: { ...ADMIN_PROFILE, email: ADMIN_PROFILE.email || `+${phone}` },
          certificates: mockCertificates,
          documents: mockDocuments,
          activity: mockActivity,
          timelines: mockTimeline,
        }
      : {
          profile: { ...EMPTY_PROFILE },
          certificates: [],
          documents: [],
          activity: [],
          timelines: {},
        };
  write(slot(userId, "data"), seed);
  return seed;
}

export function saveUserData(userId: string, data: UserData) {
  write(slot(userId, "data"), data);
}

export function clearUserData(userId: string) {
  localStorage.removeItem(slot(userId, "data"));
}
